import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { normalize } from 'normalizr';
import {
  getEditorSaveDataPromise,
  getReadyStatusPromise,
} from '../../components/editor/defaultOptions.js';
import {
  getOrgSlug,
  isDateTodayOrAfter,
  mapFinalFormErrors,
  momentToDateString,
} from '../../lib/utils.js';
import { selectCurrentOrganizationId } from '../../modules/auth.js';
import { getOrgTermsContent } from '../../content/utils.js';
import {
  createInitiative,
  deleteInitiative,
  retrieveInitiative,
  retrieveInitiativePreview,
  selectInitiative,
  selectRetrieveInitiativeState,
  updateInitiative,
} from '../../modules/initiatives.js';
import {
  retrieveOrganization,
  selectOrganization,
} from '../../modules/organizations.js';
import GroupFormContainer from '../organizations/GroupFormContainer.jsx';
import InitiativeFormHeader from '../../components/headers/PageHeaders/Initiative/InitiativeFormHeader.jsx';
import { selectProject } from '../../modules/projects.js';
import SubHeader from '../../components/SubHeader.jsx';
import InitiativeCategoryFromContainer from './InitiativeCategoryFromContainer.jsx';
import InitiativeForm from '../../components/initiatives/InitiativeForm.jsx';
import {
  deleteGroupMsg,
  deleteUngroupedEmployeeMsg,
  initiativeFormValidate,
  onParticipantsUploadChange,
  onStakeholdersUploadChange,
} from './utils/index.js';
import {
  retrieveOrgStakeholders,
  selectOrgStakeholders,
  selectOrgStakeholdersWithArchived,
  selectRetrieveOrgStakeholdersState,
} from '../../modules/orgStakeholders.js';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import {
  retrieveOrgGroups,
  selectFlattenedOrgGroups,
  selectRetrieveGroupsState,
} from '../../modules/orgGroups.js';
import {
  selectRetrieveEmployeesState,
  selectRetrieveSimplifiedEmployeesState,
} from '../../modules/employees.js';
import ManageGroupModal from '../../components/manage-group-modal/ManageGroupModal.jsx';
import ManageParticipantsModal from '../../components/manage-participants-modal/ManageParticipantsModal.jsx';
import { formParticipantSchema } from '../../lib/schema.js';

const mapErrors = mapFinalFormErrors('Failed to save initiative');
const defaultValues = {
  start_date: null,
  end_date: null,
  completion_tracked: false,
  mandatory: false,
};

const mapStateToProps = (state, ownProps) => {
  const { initiativeId } = ownProps;
  const editing = typeof ownProps.initiativeId !== 'undefined';
  const retrieveInitiativeSuccess = editing
    ? selectRetrieveInitiativeState(state).success
    : true;
  return createSelector(
    selectInitiative(initiativeId, ownProps.initiativeType),
    (initiative) => ({
      initiative,
      initiativeId,
      retrieveInitiativeSuccess,
      editing,
      isCommunication: ownProps.initiativeType === 'communication',
      project: selectProject(ownProps.projectId)(state),
      orgStakeholders: editing
        ? selectOrgStakeholdersWithArchived(state)
        : selectOrgStakeholders(state),
      flattenedGroups: selectFlattenedOrgGroups(state),
      retrieveGroupsSuccess: selectRetrieveGroupsState(state).success,
      retrieveEmployeesSuccess: selectRetrieveEmployeesState(state).success,
      organizationId: selectCurrentOrganizationId(state),
      organization: selectOrganization(state),
      retrieveOrgStakeholdersSuccess:
        selectRetrieveOrgStakeholdersState(state).success,
      retrieveEmployeesPending:
        selectRetrieveSimplifiedEmployeesState(state).pending,
      apiUrl: state.config.apiUrl,
    })
  )(state);
};

const mapDispatchToProps = {
  selectInitiative,
  createInitiative,
  deleteInitiative,
  retrieveInitiative,
  updateInitiative,
  retrieveOrgStakeholders,
  retrieveOrganization,
  retrieveOrgGroups,
  retrieveInitiativePreview,
};

/* eslint-disable camelcase */ // this form uses formDate.

const InitiativeFormContainer = (props) => {
  const {
    initiativeId,
    initiative,
    initiativeType,
    isCommunication,
    editing,
    title,
    projectId,
    organization,
    organizationId,
    retrieveOrganization,
    project,
    orgStakeholders,
    createInitiative,
    updateInitiative,
    deleteInitiative,
    retrieveInitiative,
    retrieveOrgStakeholders,
    retrieveOrgGroups,
    retrieveInitiativePreview,
    retrieveInitiativeSuccess,
    retrieveEmployeesPending,
    flattenedGroups,
    apiUrl,
  } = props;

  const resource = useMemo(
    () =>
      initiative
        ? {
            ...initiative,
            category: initiative.category.id,
          }
        : defaultValues,
    [retrieveInitiativeSuccess]
  );

  const { downloadBlob } = useFetchFile();

  const formRef = useRef(null);

  const searchParams = {
    objectType: `${initiativeType}_initiative`,
    objectId: initiativeId,
    active: !editing,
  };

  const ungroupedSearchParams = {
    ...searchParams,
    excludingGroupIds: flattenedGroups.map((g) => g.id),
  };

  const [showManageGroupModal, setShowManageGroupModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showInitiativeCategoryModal, setShowInitiativeCategoryModal] =
    useState(false);
  const [editableGroupId, setEditableGroupId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [contentEditorInstance, setContentEditorInstance] = useState(null);
  const [showManageParticipantsModal, setShowManageParticipantsModal] =
    useState(false);

  const [entities, setEntities] = useState({
    groupStakeholders: [],
    individuals: [],

    uploadStakeholdersMethod: 'manually',
    replaceStakeholdersMethod: 'add',

    uploadParticipantsMethod: 'manually',
    replaceParticipantsMethod: 'add',

    ungroupedEmployees: [],
    groups: {},
  });

  const {
    groupStakeholders,
    individuals,
    uploadParticipantsMethod,
    uploadStakeholdersMethod,
    groups,
    replaceParticipantsMethod,
    replaceStakeholdersMethod,
    ungroupedEmployees,
  } = entities;

  const updateEntities = (payload) => {
    setEntities({
      ...entities,
      ...payload,
    });
  };

  const participantsEditAllowed = () =>
    editing && resource?.end_date
      ? isDateTodayOrAfter(resource.end_date)
      : true;

  const onHideManageParticipantsModal = () =>
    setShowManageParticipantsModal(false);
  const onShowManageParticipantsModal = (id) =>
    setShowManageParticipantsModal(true);

  const onHideManageGroupModal = () => setShowManageGroupModal(false);
  const onShowManageGroupModal = (id) => {
    setEditableGroupId(id);
    setShowManageGroupModal(true);
  };

  const onHideGroupModal = () => setShowGroupModal(false);
  const onShowGroupModal = (index) => setShowGroupModal(true);

  const onHideInitiativeCategoryModal = () =>
    setShowInitiativeCategoryModal(false);
  const onShowInitiativeCategoryModal = () =>
    setShowInitiativeCategoryModal(true);

  const setParticipantsReplaceMethod = (val) =>
    updateEntities({ replaceParticipantsMethod: val });
  const setStakeholdersReplaceMethod = (val) =>
    updateEntities({ replaceStakeholdersMethod: val });

  const reAssignUngrouped = ({ id, name, employee_ids }) => {
    const commonIds = entities.ungroupedEmployees.filter((id) =>
      employee_ids.includes(id)
    );
    const restUngrouped = entities.ungroupedEmployees.filter(
      (id) => !commonIds.includes(id)
    );
    updateEntities({
      ungroupedEmployees: restUngrouped,
      groups: {
        ...entities.groups,
        [String(id)]: {
          id,
          name,
          employee_ids: commonIds,
        },
      },
    });
  };

  const onSelectGroup = (group) => {
    if (group.id in entities.groups) {
      if (!window.confirm(deleteGroupMsg)) return;

      const { [String(group.id)]: _, ...newObject } = entities.groups;
      updateEntities({ groups: newObject });
    } else {
      updateEntities({
        groups: {
          ...entities.groups,
          [String(group.id)]: {
            id: group.id,
            name: group.name,
            employee_ids: [],
          },
        },
      });
      reAssignUngrouped({ ...group, employee_ids: group.employeeIds });
    }
  };

  const onSelectAllGroupEmployees = (id, employee_ids) => {
    updateEntities({
      groups: {
        ...entities.groups,
        [String(id)]: {
          ...entities.groups[id],
          employee_ids,
        },
      },
    });
  };

  const onSelectUngroupedEmployee = (e) => {
    const isActive = entities.ungroupedEmployees.includes(e.id);
    if (isActive) {
      if (!window.confirm(deleteUngroupedEmployeeMsg)) return;
      updateEntities({
        ungroupedEmployees: entities.ungroupedEmployees.filter(
          (i) => i !== e.id
        ),
      });
    } else {
      updateEntities({
        ungroupedEmployees: [...entities.ungroupedEmployees, e.id],
      });
    }
  };

  const onSelectGroupStakeholder = (val) =>
    updateEntities({ groupStakeholders: val });
  const onSelectIndividualStakeholder = (val) =>
    updateEntities({ individuals: val });

  const onSubmitManageGroup = (group) =>
    updateEntities({
      groups: { ...entities.groups, [editableGroupId]: group },
    });

  const onSubmitManageUngrouped = (employeeIds) => {
    retrieveOrgGroups(searchParams);
    updateEntities({ ungroupedEmployees: employeeIds });
  };

  const onAddGroupSubmitSuccess = ({ entities, result }) => {
    retrieveOrgGroups(searchParams).then(() =>
      onSelectGroup(entities.orgGroups[result])
    );
  };

  const onDownloadGroupCsv = () => {
    downloadBlob(
      apiUrl,
      `/${initiativeType}_initiatives/${initiativeId}/group_structure_csv`,
      null,
      'current-group-structure-csv',
      'csv'
    );
  };

  const onDownloadStakeholdersCsv = () => {
    downloadBlob(
      apiUrl,
      `/${initiativeType}_initiatives/${initiativeId}/stakeholder_structure_csv`,
      null,
      'current-stakeholders-structure CSV',
      'csv'
    );
  };

  const onDelete = () => {
    if (
      window.confirm(
        `Are you sure you want to delete this ${getOrgTermsContent('initiative')}?`
      )
    ) {
      const id = projectId || initiative.project.id;

      deleteInitiative(initiativeType, initiativeId)
        .then(() => pushRoute('projects/show', { projectId: id }))
        .catch(mapErrors);
    }
  };

  const onCancel = () =>
    projectId
      ? pushRoute('projects/show', { projectId, slug: getOrgSlug() })
      : window.history.back();

  const onShowPreview = (id, csvFile, csvStakeholdersFile) => {
    let query;
    if (csvFile) {
      query = '?participantsCsv=true';
    } else if (csvStakeholdersFile) {
      query = '?stakeholdersCsv=true';
    } else query = '';

    window.open(
      `/${getOrgSlug()}/${initiativeType}/${id}/preview${query}`,
      '_blank'
    );
  };

  const onSubmit = (values, { isPreview = false }) => {
    setIsLoading(true);
    let submitPromise;
    const formData = new FormData(formRef.current);
    const composedFormData = new FormData();

    const mergedParticipantsData = [
      ...Object.values(groups),
      { id: null, employee_ids: ungroupedEmployees },
    ];

    const mergedStakeholdersData = [
      ...groupStakeholders,
      { id: null, individuals },
    ];
    // Convert fields to format required by API

    const data = {
      id: initiativeId || null,
      draft_origin_id: initiativeId || null,
      title: values.title || '',
      lead: values.lead || '',
      start_date: momentToDateString(formData.get('start_date')),
      // For communication initiatives, use startDate as endDate
      end_date: momentToDateString(
        isCommunication ? formData.get('start_date') : formData.get('end_date')
      ),
      initiative_category_id: values.category,
      impact: values.impact || '',
      completion_tracked: values.completion_tracked || '',
      mandatory: values.mandatory,
      manually: uploadParticipantsMethod === 'manually',
      manually_stakeholders: uploadStakeholdersMethod === 'manually',
      groups_data: JSON.stringify(mergedParticipantsData),
      stakeholders_data: JSON.stringify(mergedStakeholdersData),
    };

    for (const key in data) {
      composedFormData.append(`initiative[${key}]`, data[key]);
    }

    if (uploadParticipantsMethod === 'csv') {
      composedFormData.append('initiative[csv_file]', formData.get('csv_file'));
      composedFormData.append(
        'initiative[merge_to_existing]',
        replaceParticipantsMethod === 'add'
      );
    }

    if (uploadStakeholdersMethod === 'csv') {
      composedFormData.append(
        'initiative[csv_stakeholders_file]',
        formData.get('csv_stakeholders_file')
      );
      composedFormData.append(
        'initiative[stakeholders_merge]',
        replaceStakeholdersMethod === 'add'
      );
    }

    const saveData = () => {
      if (!isPreview) {
        if (!editing) {
          composedFormData.append('initiative[project_id]', projectId);
          submitPromise = createInitiative(initiativeType, composedFormData);
        } else {
          submitPromise = updateInitiative(
            initiativeType,
            initiativeId,
            composedFormData
          );
        }
      } else {
        composedFormData.append(
          'initiative[project_id]',
          editing ? initiative.project.id : projectId
        );
        submitPromise = retrieveInitiativePreview(
          initiativeType,
          composedFormData
        );
      }

      return submitPromise
        .then(({ result }) => {
          const id = /\d+$/.exec(result)[0];

          if (!isPreview) {
            return pushRoute('initiatives/show', {
              initiativeId: id,
              initiativeType,
            });
          }
          onShowPreview(id, values.csv_file, values.csv_stakeholders_file);

          setIsLoading(false);
        })
        .catch(mapErrors)
        .finally(() => setIsLoading(false));
    };

    if (isCommunication && !isPreview) {
      return getReadyStatusPromise(contentEditorInstance).then(() =>
        getEditorSaveDataPromise(contentEditorInstance).then((editorData) => {
          composedFormData.append(
            'initiative[content]',
            JSON.stringify(editorData)
          );
          return saveData();
        })
      );
    }
    return saveData();
  };

  const validate = (values) => initiativeFormValidate(values, isCommunication);

  useEffect(() => {
    if (editing) {
      retrieveInitiative(initiativeType, initiativeId, true);
      retrieveOrgGroups(searchParams);
    } else {
      retrieveOrgGroups(searchParams);
    }

    retrieveOrgStakeholders();
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (initiative && 'groups_data' in initiative) {
      const { groups_data, stakeholders_data } = initiative;
      let ungroupedEmployees = [];
      let groups = {};
      let groupStakeholders = [];
      let individuals = [];

      if (
        initiative &&
        groups_data &&
        groups_data.length &&
        !(entities.groups.length || entities.ungroupedEmployees.length)
      ) {
        const filteredGroups = groups_data.filter((g) => g.id !== null);
        const ungrouped = groups_data.find((g) => g.id === null);
        groups =
          normalize(filteredGroups, [formParticipantSchema]).entities.groups ||
          {};
        ungroupedEmployees = ungrouped ? ungrouped.employee_ids : [];
      }

      if (initiative && stakeholders_data?.length) {
        const withoutParent = stakeholders_data.find((s) => s.id === null);
        groupStakeholders = stakeholders_data.filter((s) => s.id !== null);
        individuals = withoutParent ? withoutParent.individuals : [];
      }

      updateEntities({
        ungroupedEmployees,
        groups,
        groupStakeholders,
        individuals,
      });
    }
  }, [retrieveInitiativeSuccess, initiativeId]);

  useEffect(() => {
    if (!organization && organizationId) {
      retrieveOrganization(organizationId);
    }
  }, [organizationId]);

  return (
    <React.Fragment>
      <InitiativeFormHeader project={project} initiativeType={initiativeType} />
      <SubHeader flexJustify small>
        <h2>{title}</h2>
        {editing && (
          <Button
            className="m-l-12"
            bsStyle="danger"
            disabled={isLoading}
            onClick={onDelete}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        )}
      </SubHeader>
      <div
        style={{
          position: 'relative',
          opacity: retrieveEmployeesPending ? 0.5 : 1,
          cursor: retrieveEmployeesPending ? 'wait' : 'auto',
        }}
      >
        <Grid>
          <Row>
            <Col sm={12}>
              <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
                <Row>
                  <Col sm={10} smOffset={1}>
                    <Form
                      formRef={formRef}
                      editing={editing}
                      onSubmit={onSubmit}
                      onShowPreview={onSubmit}
                      onCancel={onCancel}
                      validate={validate}
                      isLoading={isLoading}
                      initiativeType={initiativeType}
                      isCommunication={isCommunication}
                      entities={entities}
                      initialValues={resource}
                      orgStakeholders={orgStakeholders}
                      organization={organization}
                      onDownloadStakeholdersCsv={onDownloadStakeholdersCsv}
                      onDownloadGroupCsv={onDownloadGroupCsv}
                      onSelectGroup={onSelectGroup}
                      onSelectAllGroupEmployees={onSelectAllGroupEmployees}
                      onSelectUngroupedEmployee={onSelectUngroupedEmployee}
                      onSelectIndividualStakeholder={
                        onSelectIndividualStakeholder
                      }
                      onSelectGroupStakeholder={onSelectGroupStakeholder}
                      onShowGroupModal={onShowGroupModal}
                      onShowInitiativeCategoryModal={
                        onShowInitiativeCategoryModal
                      }
                      onShowManageGroupModal={onShowManageGroupModal}
                      onShowManageParticipantsModal={
                        onShowManageParticipantsModal
                      }
                      participantsEditAllowed={participantsEditAllowed()}
                      setParticipantsReplaceMethod={
                        setParticipantsReplaceMethod
                      }
                      setStakeholdersReplaceMethod={
                        setStakeholdersReplaceMethod
                      }
                      setUploadStakeholdersMethod={(m) =>
                        onStakeholdersUploadChange(m, updateEntities)
                      }
                      setUploadParticipantsMethod={(m) =>
                        onParticipantsUploadChange(m, updateEntities)
                      }
                      setContentEditorInstance={setContentEditorInstance}
                      ungroupedSearchParams={ungroupedSearchParams}
                      component={InitiativeForm}
                    />
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Grid>
      </div>

      <GroupFormContainer
        onSubmitSuccess={onAddGroupSubmitSuccess}
        showFormModal={showGroupModal}
        onHideFormModal={onHideGroupModal}
      />

      <InitiativeCategoryFromContainer
        showFormModal={showInitiativeCategoryModal}
        onHideFormModal={onHideInitiativeCategoryModal}
        initiativeType={initiativeType}
      />

      <ManageGroupModal
        showFormModal={showManageGroupModal}
        onHideFormModal={onHideManageGroupModal}
        group={entities.groups[editableGroupId]}
        onSubmit={onSubmitManageGroup}
        additionalSearchParams={searchParams}
      />

      <ManageParticipantsModal
        employeeIds={entities.ungroupedEmployees}
        showFormModal={showManageParticipantsModal}
        onHideFormModal={onHideManageParticipantsModal}
        additionalSearchParams={ungroupedSearchParams}
        onSubmit={onSubmitManageUngrouped}
        title="Impacted People"
      />
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InitiativeFormContainer);
